import React from 'react';
import { DateTime } from 'luxon';
import _compact from 'lodash/compact';
import _filter from 'lodash/filter';
import _findKey from 'lodash/findKey';
import _size from 'lodash/size';
import _some from 'lodash/some';

import { useServiceAuth } from '@lifeomic/phc-web-toolkit/src/hooks';
import {
  useCreateObservationFromShortVariantMutation,
  useCreateObservationFromCopyNumberVariantMutation,
  useCreateObservationFromExpressionVariantMutation,
  useCreateObservationFromStructuralVariantMutation,
} from '@lifeomic/phc-web-toolkit/src/graphql/mutations/__generated__/CreateObservationFromVariant.graphql';
import { GetObservationDocument } from '@lifeomic/phc-web-toolkit/src/graphql/queries/__generated__/GetObservation.graphql';
import {
  BaseSelectedVariant,
  VariantSelectionStateType,
  SelectedVariant,
  SelectedShortVariant,
  SelectedCopyNumberVariant,
  SelectedExpressionVariant,
  SelectedStructuralVariant,
} from './types';
import { SetType } from '../types';
import { getGenomicsReferenceSequence } from './getGenomicsReferenceSequence';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import DiagnosticReport = fhir.DiagnosticReport;
import { EffectTypes } from '../../../../src/components/pages/SubjectSearch/Search/Filters/Genomics/StructuralVariants/types';
import { CopyNumberVariantTypes } from '../../../../src/components/pages/SubjectSearch/Search/Filters/Genomics/CopyNumberVariants/types';
import { reportInfoToBugsnag } from '../../../../src/util/errorReporting/directReporting';

interface UseSelectedVariantsProps {
  datasetId: string;
  subjectId: string;
}

/**
 * @param observation variant selection data from GQL
 * @return variant metadata object, will be undefined if no variantId found
 */
const transObservationToVariantSelectionState = (
  observation: any,
): Partial<BaseSelectedVariant> | undefined => {
  const variantId: string | undefined = observation.extension?.find(
    (extension: any) =>
      extension.url === 'http://hl7.org/fhir/StructureDefinition/variant-id',
  )?.valueString;

  const setType: SetType | undefined = observation.extension?.find(
    (extension: any) =>
      extension.url ===
      'http://hl7.org/fhir/StructureDefinition/variant-set-type',
  )?.valueString;

  return variantId ? { variantId, setType } : undefined;
};

const getEffectCoding = (effect: EffectTypes) => {
  switch (effect) {
    case 'fusion':
    case 'Fusion':
      return {
        code: 'SO:0000806',
        system: 'http://sequenceontology.org',
        display: 'fusion',
      };
    case 'rearrangement':
    case 'Rearrangement':
      return {
        code: 'SO:0001872',
        system: 'http://sequenceontology.org',
        display: 'rearrangement',
      };
    case 'truncation':
    case 'Truncation':
      return {
        code: 'SO:0001906',
        system: 'http://sequenceontology.org',
        display: 'truncation',
      };
    case 'deletion':
    case 'Deletion':
      return {
        code: 'SO:0000159',
        system: 'http://sequenceontology.org',
        display: 'deletion',
      };
    case 'duplication':
    case 'Duplication':
      return {
        code: 'SO:1000035',
        system: 'http://sequenceontology.org',
        display: 'duplication',
      };
    default:
      reportInfoToBugsnag(`Effect (${effect}) not in EffectTypes`, {
        fileName: 'useSelectedVariants.tsx',
        lineNumber: 101,
      });
      return {
        display: effect,
      };
  }
};

const getCopyNumberType = (type: CopyNumberVariantTypes) => {
  return {
    code: type,
    system: 'http://lifeomic.com/fhir/copyNumberTypeValue',
    display: type,
  };
};

// whether a given observation is about a manual finding
const isOverallInterpretation = (observation: any): boolean => {
  // copied from PR 3032, should refactor out later
  return observation?.code?.coding?.find(
    (coding: fhir.Coding) =>
      coding.code === '51968-6' && coding.system === 'http://loinc.org',
  );
};

const OBSERVATION_REFERENCE_PREFIX: string = 'Observation/';

export class VariantSelection {
  // TODO support other variant types besides SelectedShortVariant
  /**
   * specialized state store for row selection. in order to store both state and
   * persistent FHIR resource, it will add semantics as prefix to the key:
   *
   *   * 'Observation/xyz' - refer to a FHIR observation resource
   *   * 'abcdef' - refer to a state object like a variant selection
   */
  variants: VariantSelectionStateType;

  constructor(variants: any) {
    this.variants = variants;
  }

  /**
   * create a new object of selection from given dictionary.
   * @param store dictionary of observation id maps to variant selection
   * @returns a new object with loaded variants (empty metadata, variantId only)
   */
  init(store: VariantSelectionStateType): VariantSelection {
    for (const ref in store) {
      const variantId = store[ref].variantId;
      // avoid storing the same variant selection with different key.
      // this might happen when manual selection occurs prior to fetching result
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      if (variantId in this.variants) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        delete this.variants[variantId];
      }
    }

    return new VariantSelection(Object.assign(this.variants, store));
  }

  /**
   * create a new object to trigger re-rendering
   * @param variant variant object from a row data
   * @returns a new object with updated variants selection
   */
  copyWithToggled(variant: BaseSelectedVariant): VariantSelection {
    const key = variant.variantId;
    if (this.isSelected(key)) {
      // case 1: not persisted
      if (key in this.variants) {
        delete this.variants[key];
        // case 2: persisted
      } else {
        const ref = _findKey(this.variants, { variantId: key });
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        delete this.variants[ref];
      }
    } else {
      // TODO rare case: how to avoid persisting a previously persisted but
      // deselected-then-selected variant?
      this.variants[key] = variant;
    }

    return new VariantSelection(this.variants);
  }

  static empty(): VariantSelection {
    return new VariantSelection({});
  }

  isSelected(variantId: string): boolean {
    return _some(this.variants, { variantId });
  }

  _persistedVariants(): string[] {
    const variantIds: string[] = [];

    for (const [key, value] of Object.entries(this.variants)) {
      if (key.startsWith(OBSERVATION_REFERENCE_PREFIX)) {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        variantIds.push(value.variantId);
      }
    }

    return variantIds;
  }

  get count(): number {
    return _size(this.variants);
  }

  /**
   * save any non-persistent variant selection as an observation.
   */
  async persist(
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    createTargetedVariant: (SelectedVariant) => Promise<string>,
  ): Promise<any> {
    const persistedVariantIds = this._persistedVariants();

    return Promise.all(
      _filter(this.variants, (_, key) => {
        return !key.startsWith(OBSERVATION_REFERENCE_PREFIX);
      }).map(async (variant: Partial<BaseSelectedVariant>) => {
        // only save non-persistent variant selection as an observation
        if (!_some(persistedVariantIds, id => id === variant.variantId)) {
          const observationId = await createTargetedVariant(variant);
          this.variants[`${OBSERVATION_REFERENCE_PREFIX}${observationId}`] =
            variant;
        }

        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        delete this.variants[variant.variantId];
      }),
    );
  }

  /**
   * @return an array of unique references points to persisted variant selections.
   */
  get references(): string[] {
    return Object.keys(this.variants).filter(
      (value, index, self) =>
        value.substring(0, 12) === OBSERVATION_REFERENCE_PREFIX &&
        self.indexOf(value) === index,
    );
  }
}

export const useSelectedVariants = (props: UseSelectedVariantsProps) => {
  const serviceAuth = useServiceAuth();
  const apolloClient = useApolloClient();
  const user = useSelector(userSelectors.selectActiveUser);

  const { datasetId, subjectId } = props;

  // copied from packages/phc-web-toolkit/src/components/TrialMatching/hooks.ts
  // TODO refactor for reusing
  const context = React.useMemo(() => {
    return {
      headers: {
        'LifeOmic-Account': serviceAuth.account,
        Authorization: `Bearer ${serviceAuth.authToken}`,
      },
    };
  }, [serviceAuth.account, serviceAuth.authToken]);

  const [createObservationFromShortVariantMutation] =
    useCreateObservationFromShortVariantMutation({ context });
  const [createObservationFromCopyNumberVariantMutation] =
    useCreateObservationFromCopyNumberVariantMutation({ context });
  const [createObservationFromExpressionVariantMutation] =
    useCreateObservationFromExpressionVariantMutation({ context });
  const [createObservationFromStructuralVariantMutation] =
    useCreateObservationFromStructuralVariantMutation({ context });
  const [variantSelection, updateVariantSelection] =
    React.useState<VariantSelection>(VariantSelection.empty());
  const [error, setError] = React.useState(false);

  const createTargetedVariant = (
    selectedVariant: SelectedVariant,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  ): Promise<string> => {
    const loincSequenceClass =
      selectedVariant.sequenceType === 'somatic' ? 'LA6684-0' : 'LA6683-2';
    const effectiveDateTime = DateTime.now().toISO();

    switch (selectedVariant.setType) {
      case 'shortVariant': {
        const {
          variantId,
          ncChange,
          aaChange,
          gene,
          chromosome,
          position,
          testId,
          buildReference,
          sequenceType,
          sequenceId,
          refAllele,
          altAllele,
          setType,
          alleleFrequency,
        } = selectedVariant as SelectedShortVariant;
        const refSeqId = getGenomicsReferenceSequence(
          buildReference,
          chromosome,
        );

        // TODO: Currently wiring this immediately on selection. Need to sitdown with Sam to figure out the proper flow. SS
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return createObservationFromShortVariantMutation({
          variables: {
            user: user.id,
            effectiveDateTime,
            datasetId,
            variantId,
            subjectRef: `Patient/${subjectId}`,
            sequenceId,
            testId,
            ncChange: `${refSeqId || 'unknown'}:${ncChange}`,
            aaChange: aaChange || 'unknown',
            gene,
            refSeqId,
            loincSequenceClass,
            sequenceType,
            position: {
              value: position,
            },
            refAllele,
            altAllele,
            setType,
            alleleFrequency,
          },
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }).then(result => result.data.createObservation.id);
      }
      case 'copyNumberVariant': {
        const {
          variantId,
          geneId,
          startPosition,
          endPosition,
          copyNumberValue,
          type,
          testId,
          sequenceId,
          sequenceType,
        } = selectedVariant as SelectedCopyNumberVariant;

        const typeCoding = [getCopyNumberType(type)];

        // TODO: Currently wiring this immediately on selection. Need to sitdown with Sam to figure out the proper flow. SS
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return createObservationFromCopyNumberVariantMutation({
          variables: {
            user: user.id,
            datasetId,
            effectiveDateTime,
            variantId,
            subjectRef: `Patient/${subjectId}`,
            geneId,
            position: {
              low: { value: startPosition },
              high: { value: endPosition },
            },
            copyNumberValue,
            typeCoding,
            loincSequenceClass,
            sequenceId,
            sequenceType,
            testId,
          },
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }).then(result => result.data.createObservation.id);
      }
      case 'expression': {
        const {
          variantId,
          gene,
          expression,
          expressionUnit,
          sequenceId,
          sequenceType,
          testId,
        } = selectedVariant as SelectedExpressionVariant;

        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return createObservationFromExpressionVariantMutation({
          variables: {
            user: user.id,
            datasetId,
            effectiveDateTime,
            variantId,
            subjectRef: `Patient/${subjectId}`,
            geneId: gene,
            expressionValue: expression,
            expressionUnitCode: expressionUnit,
            expressionUnitSystem: `http://lifeomic.com/fhir/${expressionUnit}`,
            sequenceId,
            sequenceType,
            loincSequenceClass,
            testId,
          },
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }).then(result => result.data.createObservation.id);
      }
      case 'structuralVariant': {
        const {
          effect,
          variantId,
          geneId1,
          geneId2,
          testId,
          startPosition1,
          endPosition1,
          startPosition2,
          endPosition2,
          sequenceId,
          sequenceType,
        } = selectedVariant as SelectedStructuralVariant;

        // Effect
        const effectCoding = [getEffectCoding(effect)];

        // either geneId1 or geneId2 could be null or empty
        const geneIdCoding = _compact([geneId1, geneId2]).map(geneId => {
          return {
            code: geneId,
            system: 'http://www.genenames.org/geneId',
          };
        });

        // TODO: Currently wiring this immediately on selection. Need to sitdown with Sam to figure out the proper flow. SS
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return createObservationFromStructuralVariantMutation({
          variables: {
            user: user.id,
            datasetId,
            effectiveDateTime,
            subjectRef: `Patient/${subjectId}`,
            variantId,
            testId,
            effectCoding,
            geneIdCoding,
            position1: {
              low: { value: startPosition1 },
              high: { value: endPosition1 },
            },
            position2: {
              low: { value: startPosition2 },
              high: { value: endPosition2 },
            },
            sequenceId,
            sequenceType,
            loincSequenceClass,
          },
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        }).then(result => result.data.createObservation.id);
      }
    }
  };

  /**
   * @returns array of reference of overall interpretation observation
   */
  const refreshTargetedVariants = React.useCallback(
    async (diagnosticReport: DiagnosticReport) => {
      // empty variant selection first
      const newVariantSelection = VariantSelection.empty();
      const overallInterpretations: string[] = [];

      updateVariantSelection(newVariantSelection);
      setError(false);
      if (diagnosticReport.result !== null) {
        const store: VariantSelectionStateType = {};
        for (const observationId of diagnosticReport.result?.map(ref => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          return ref.reference.indexOf(OBSERVATION_REFERENCE_PREFIX) === 0
            ? // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              ref.reference.substring(12)
            : ref.reference;
        }) || []) {
          const { data } = await apolloClient.query({
            query: GetObservationDocument,
            variables: { id: observationId },
            context,
          });

          if (!data?.observation) {
            setError(true);
          }

          const variantSelectionState = transObservationToVariantSelectionState(
            data.observation || {},
          );

          if (variantSelectionState) {
            store[`${OBSERVATION_REFERENCE_PREFIX}${observationId}`] =
              variantSelectionState;
          } else if (isOverallInterpretation(data.observation)) {
            overallInterpretations.push(
              `${OBSERVATION_REFERENCE_PREFIX}${observationId}`,
            );
          }
        }

        // populate variantSelection with variantIds map to nulls (means init from fhir)
        // TODO figure out whether it needs to populate the whole variant metadata
        await updateVariantSelection(newVariantSelection.init(store));
        return overallInterpretations;
      }
    },
    [apolloClient, context],
  );

  return {
    variantSelection,
    updateVariantSelection,
    createTargetedVariant,
    refreshTargetedVariants,
    error,
  };
};
